import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CreditCard, Search, Activity, RefreshCw } from "lucide-react";

import ManageTierModal from "@/components/site-admin/ManageTierModal";
import TrialCodesSection from "@/components/site-admin/TrialCodesSection";
import LandingIntakeControls from "@/components/site-admin/LandingIntakeControls";

/**
 * Site Admin → Subscriptions tab (iter42, P4 groundwork; iter43d split).
 *
 * Top-level orchestrator for the Subscriptions tab. Renders:
 *  - tier roll-up cards (Free / Paid / Comp)
 *  - searchable + filterable league table with "Manage" action per row
 *  - Activity Log (collapsible) — chronological audit feed
 *  - Crown Points Sandbox (collapsible) — dev preview tool
 *
 * Sub-components (extracted iter43d for maintainability):
 *  - ManageTierModal — tier change modal with conditional comp/paid/free fields
 *  - CrownPointsSandbox — dev seeder + tiebreaker preview
 *
 * See /app/memory/P4_MOBILE_UNIFIED_IDENTITY.md for the full spec.
 */

const TIER_BADGE = {
    free: { label: "Free", className: "bg-zinc-700 text-zinc-200 border-zinc-500" },
    paid: { label: "Pro+", className: "bg-neon text-black border-neon" },
    comp: { label: "Comp", className: "bg-volt text-black border-volt" },
};

function TierBadge({ tier, expired }) {
    const cfg = TIER_BADGE[tier] || TIER_BADGE.free;
    return (
        <span
            data-testid={`subscription-tier-badge-${tier}`}
            className={`pill border-2 text-[10px] font-bold uppercase ${cfg.className}`}
        >
            {cfg.label}
            {expired && <span className="ml-1 text-blaze">· EXPIRED</span>}
        </span>
    );
}

function formatExp(iso) {
    if (!iso) return "no expiry";
    try {
        return new Date(iso).toLocaleDateString(undefined, { dateStyle: "medium" });
    } catch {
        return iso;
    }
}

export default function SubscriptionsTab() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    // iter43f — Default to "paid + comp" instead of "all" so the page isn't
    // dominated by hundreds of free leagues. Special value "active" filters
    // to the actually-active tiers; users can flip to "all" to see free.
    const [tierFilter, setTierFilter] = useState("active");
    const [showAll, setShowAll] = useState(false);
    const [editingLeague, setEditingLeague] = useState(null); // row object
    const [saving, setSaving] = useState(false);

    // Activity Log (iter42b) — chronological feed of every tier change.
    const [activity, setActivity] = useState([]);
    const [activityLoading, setActivityLoading] = useState(false);
    const [showActivity, setShowActivity] = useState(false);

    const loadActivity = useCallback(async () => {
        setActivityLoading(true);
        try {
            const r = await api.siteSubscriptionsActivity(50);
            setActivity(r.entries || []);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to load activity");
        }
        setActivityLoading(false);
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.siteListSubscriptions();
            setRows(r);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to load subscriptions");
        }
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    const filtered = rows.filter(r => {
        const eff = r.subscription.effective_tier;
        // "active" = paid OR comp (the interesting tiers)
        if (tierFilter === "active" && eff === "free") return false;
        if (tierFilter !== "all" && tierFilter !== "active" && eff !== tierFilter) return false;
        if (search.trim()) {
            const q = search.trim().toLowerCase();
            if (!(r.name || "").toLowerCase().includes(q) && !(r.id || "").includes(q)) return false;
        }
        return true;
    });

    // iter43f — Cap visible rows at 25 to keep the page snappy. "Show all"
    // expands. Always shows everything if a search query is active (you
    // searched for something specific).
    const ROW_CAP = 25;
    const isSearching = !!search.trim();
    const visible = (showAll || isSearching) ? filtered : filtered.slice(0, ROW_CAP);
    const hiddenCount = filtered.length - visible.length;

    const counts = rows.reduce((acc, r) => {
        const t = r.subscription.effective_tier;
        acc[t] = (acc[t] || 0) + 1;
        return acc;
    }, { free: 0, paid: 0, comp: 0 });

    return (
        <div className="space-y-5" data-testid="subscriptions-tab-wrapper">
            <div className="brutal-card p-5 space-y-2">
                <h2 className="font-display font-extrabold text-xl uppercase flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-volt" />
                    Rivalry Subscriptions
                </h2>
                <p className="text-xs text-zinc-400 leading-relaxed">
                    Manage which leagues have <span className="text-volt font-bold">comp</span> (complimentary, no billing) status or <span className="text-neon font-bold">Pro+</span> overrides. Comp is granted manually for beta testers, partnerships, and future promo-code redemptions. Pro+ is currently a manual override — it'll switch to Stripe-webhook-only when Phase B ships. Free = today's default (no multi-tournament).
                </p>
            </div>

            <div className="grid grid-cols-3 gap-3" data-testid="subscriptions-rollup">
                <div className="brutal-card-light p-3 text-center">
                    <p className="text-[10px] uppercase tracking-widest text-zinc-500">Free</p>
                    <p className="font-display font-extrabold text-2xl" data-testid="rollup-free">{counts.free}</p>
                </div>
                <div className="brutal-card-light p-3 text-center border-volt/40">
                    <p className="text-[10px] uppercase tracking-widest text-volt">Comp</p>
                    <p className="font-display font-extrabold text-2xl text-volt" data-testid="rollup-comp">{counts.comp}</p>
                </div>
                <div className="brutal-card-light p-3 text-center border-neon/40">
                    <p className="text-[10px] uppercase tracking-widest text-neon">Pro+</p>
                    <p className="font-display font-extrabold text-2xl text-neon" data-testid="rollup-paid">{counts.paid}</p>
                </div>
            </div>

            {/* iter87.80 — Landing gate + per-tier acceptance moved here from
                the Requests tab to consolidate rivalry-ops controls next to
                the subscription counters. */}
            <LandingIntakeControls />

            {/* iter71 — Trial Codes CRUD lives just below the intake controls
                so operators can see gate-state, tier acceptance, then the
                actual comp-code source of truth in one flow. */}
            <TrialCodesSection />

            <div className="brutal-card p-3 flex flex-wrap items-center gap-3" data-testid="subscriptions-filters">
                <div className="flex-1 min-w-[200px] relative">
                    <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                    <input
                        data-testid="subscriptions-search-input"
                        type="text"
                        placeholder="Search by rivalry name or id…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="brutal-input w-full pl-9 text-sm"
                    />
                </div>
                <select
                    data-testid="subscriptions-tier-filter"
                    value={tierFilter}
                    onChange={(e) => { setTierFilter(e.target.value); setShowAll(false); }}
                    className="brutal-input text-sm"
                >
                    <option value="active">Pro+ + Comp ({counts.paid + counts.comp})</option>
                    <option value="all">All tiers ({rows.length})</option>
                    <option value="free">Free only ({counts.free})</option>
                    <option value="paid">Pro+ only ({counts.paid})</option>
                    <option value="comp">Comp only ({counts.comp})</option>
                </select>
            </div>

            {loading && (
                <div className="brutal-card p-5 text-center text-zinc-400 text-sm" data-testid="subscriptions-loading">
                    Loading…
                </div>
            )}

            {!loading && filtered.length === 0 && (
                <div className="brutal-card p-5 text-center text-zinc-500 text-sm" data-testid="subscriptions-empty">
                    No leagues match.
                </div>
            )}

            {!loading && filtered.length > 0 && (
                <div className="brutal-card p-0 overflow-hidden" data-testid="subscriptions-table-wrapper">
                    <div className="overflow-x-auto overflow-y-auto" style={{ maxHeight: "60vh" }}>
                        <table className="w-full text-sm" data-testid="subscriptions-table">
                            <thead className="sticky top-0 bg-ink z-10">
                                <tr className="text-left text-zinc-500 uppercase text-[10px] tracking-wider border-b-2 border-[#333]">
                                    <th className="p-3">Rivalry</th>
                                    <th className="p-3">Tier</th>
                                    <th className="p-3">Reason</th>
                                    <th className="p-3">Expires</th>
                                    <th className="p-3">Granted by</th>
                                    <th className="p-3 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visible.map((row) => {
                                    const sub = row.subscription;
                                    return (
                                        <tr
                                            key={row.id}
                                            data-testid={`subscriptions-row-${row.id}`}
                                            className="border-b border-[#222] hover:bg-zinc-900/40"
                                        >
                                            <td className="p-3">
                                                <p className="font-bold">{row.name}</p>
                                                <p className="text-[10px] text-zinc-500 font-mono">{row.id.slice(0, 8)}…</p>
                                            </td>
                                            <td className="p-3">
                                                <TierBadge tier={sub.effective_tier} expired={sub.expired} />
                                            </td>
                                            <td className="p-3 text-xs text-zinc-300 max-w-[200px] truncate" title={sub.comp_reason || ""}>
                                                {sub.comp_reason || <span className="text-zinc-600">—</span>}
                                            </td>
                                            <td className="p-3 text-xs">
                                                {sub.stored_tier === "comp" ? (
                                                    <span className={sub.expired ? "text-blaze font-bold" : "text-zinc-300"}>
                                                        {formatExp(sub.comp_expires_at)}
                                                    </span>
                                                ) : (
                                                    <span className="text-zinc-600">—</span>
                                                )}
                                            </td>
                                            <td className="p-3 text-xs text-zinc-400 max-w-[200px] truncate" title={sub.comp_granted_by || ""}>
                                                {sub.comp_granted_by || <span className="text-zinc-600">—</span>}
                                            </td>
                                            <td className="p-3 text-right">
                                                <button
                                                    data-testid={`manage-tier-btn-${row.id}`}
                                                    onClick={() => setEditingLeague(row)}
                                                    className="brutal-btn-ghost text-xs"
                                                >
                                                    Manage
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    {hiddenCount > 0 && (
                        <div
                            className="p-3 border-t-2 border-[#333] text-center bg-zinc-900/30"
                            data-testid="subscriptions-show-all-row"
                        >
                            <button
                                data-testid="subscriptions-show-all-btn"
                                onClick={() => setShowAll(true)}
                                className="brutal-btn-ghost text-xs"
                            >
                                Show all {filtered.length} → ({hiddenCount} hidden)
                            </button>
                        </div>
                    )}
                </div>
            )}

            <div className="brutal-card p-0 overflow-hidden" data-testid="comp-activity-log-card">
                <button
                    data-testid="comp-activity-toggle-btn"
                    onClick={() => {
                        const next = !showActivity;
                        setShowActivity(next);
                        if (next && activity.length === 0) loadActivity();
                    }}
                    className="w-full p-4 flex items-center justify-between hover:bg-zinc-900/30"
                >
                    <span className="flex items-center gap-2 font-display font-extrabold uppercase text-sm">
                        <Activity className="w-4 h-4 text-volt" />
                        Activity Log
                        <span className="text-[10px] text-zinc-500 normal-case font-normal tracking-normal ml-1">
                            chronological audit of every tier change
                        </span>
                    </span>
                    <span className="text-zinc-500 text-xs">
                        {showActivity ? "Hide ▲" : "Show ▼"}
                    </span>
                </button>

                {showActivity && (
                    <div className="border-t-2 border-[#333] p-4 space-y-3" data-testid="comp-activity-log-body">
                        <div className="flex justify-between items-center">
                            <p className="text-[11px] text-zinc-500">
                                Most recent first. Use for GTM attribution — filter on `promo:` reasons to track which campaigns converted.
                            </p>
                            <button
                                data-testid="comp-activity-refresh-btn"
                                onClick={loadActivity}
                                disabled={activityLoading}
                                className="brutal-btn-ghost text-[10px] flex items-center gap-1 disabled:opacity-40"
                            >
                                <RefreshCw className={`w-3 h-3 ${activityLoading ? "animate-spin" : ""}`} />
                                Refresh
                            </button>
                        </div>

                        {activityLoading && (
                            <p className="text-xs text-zinc-500 py-4 text-center" data-testid="comp-activity-loading">Loading…</p>
                        )}

                        {!activityLoading && activity.length === 0 && (
                            <p className="text-xs text-zinc-500 py-4 text-center" data-testid="comp-activity-empty">
                                No tier changes recorded yet. Grant a comp to start the audit trail.
                            </p>
                        )}

                        {!activityLoading && activity.length > 0 && (
                            <ul className="space-y-2 max-h-96 overflow-y-auto pr-1" data-testid="comp-activity-list">
                                {activity.map((e) => {
                                    const when = e.created_at ? new Date(e.created_at) : null;
                                    const whenStr = when ? when.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—";
                                    const isComp = e.summary.includes("→ comp");
                                    const isPaid = e.summary.includes("→ paid");
                                    const isFree = e.summary.includes("→ free");
                                    const accent = isComp ? "border-l-volt" : isPaid ? "border-l-neon" : isFree ? "border-l-zinc-500" : "border-l-zinc-700";
                                    return (
                                        <li
                                            key={e.id}
                                            data-testid={`comp-activity-row-${e.id}`}
                                            className={`brutal-card-light p-3 border-l-4 ${accent}`}
                                        >
                                            <div className="flex items-baseline justify-between gap-2 flex-wrap">
                                                <p className="font-bold text-sm truncate" data-testid={`comp-activity-league-${e.id}`}>
                                                    {e.league_name}
                                                </p>
                                                <span className="text-[10px] text-zinc-500 font-mono whitespace-nowrap">{whenStr}</span>
                                            </div>
                                            <p className="text-xs text-zinc-300 mt-1">{e.summary}</p>
                                            <p className="text-[10px] text-zinc-500 mt-1">
                                                By <span className="text-zinc-400 font-mono">{e.actor || "—"}</span>
                                                <span className="mx-1">·</span>
                                                League ID <span className="font-mono">{e.league_id.slice(0, 8)}…</span>
                                            </p>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>
                )}
            </div>

            {editingLeague && (
                <ManageTierModal
                    league={editingLeague}
                    saving={saving}
                    onClose={() => setEditingLeague(null)}
                    onSave={async (payload) => {
                        setSaving(true);
                        try {
                            const r = await api.siteSetSubscriptionTier(editingLeague.id, payload);
                            setRows((prev) => prev.map((row) =>
                                row.id === editingLeague.id
                                    ? { ...row, subscription: r.subscription }
                                    : row
                            ));
                            toast.success(`${editingLeague.name} → ${payload.tier.toUpperCase()}`);
                            setEditingLeague(null);
                            if (showActivity) loadActivity();
                        } catch (err) {
                            toast.error(err.response?.data?.detail || "Failed to save");
                        }
                        setSaving(false);
                    }}
                />
            )}
        </div>
    );
}
